import { useState, useRef } from 'react';
import { X, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MAX_FILE_SIZE = 8 * 1024 * 1024;
const MAX_DIMENSION = 512;
const JPEG_QUALITY = 0.82;

interface ImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
  /**
   * When true, the image is compressed in-browser to a data URL and returned via onChange.
   * The parent is responsible for persisting it (e.g. to the database), which keeps the
   * photo durable across sessions and deploys. When false, uploads go to Cloudinary.
   */
  useDataUrl?: boolean;
  /** Longest edge kept when storing the image in the database. */
  maxEdge?: number;
  quality?: number;
  /** Fill transparent pixels before JPEG encoding. */
  background?: string;
  buttonLabel?: string;
  /** Tall article cards should not be cropped to a square. */
  variant?: 'square' | 'card';
  inputTestId?: string;
}

/**
 * Downscale and compress an image file into a JPEG data URL so it stays small
 * enough to store directly in the database.
 */
function compressImageToDataUrl(
  file: File,
  maxEdge: number,
  quality: number,
  background?: string
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const longest = Math.max(width, height);
        if (longest > maxEdge) {
          const scale = maxEdge / longest;
          width = Math.max(1, Math.round(width * scale));
          height = Math.max(1, Math.round(height * scale));
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not process image'));
          return;
        }
        if (background) {
          ctx.fillStyle = background;
          ctx.fillRect(0, 0, width, height);
        }
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => reject(new Error('Could not load image'));
      img.src = reader.result as string;
    };
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export function ImageUpload({
  value,
  onChange,
  useDataUrl = false,
  maxEdge = MAX_DIMENSION,
  quality = JPEG_QUALITY,
  background,
  buttonLabel,
  variant = 'square',
  inputTestId,
}: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const displayUrl = localPreview || value;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('Image must be less than 8MB');
      return;
    }

    setError(null);
    setIsUploading(true);

    try {
      if (useDataUrl) {
        const dataUrl = await compressImageToDataUrl(file, maxEdge, quality, background);
        setLocalPreview(dataUrl);
        onChange(dataUrl);
      } else {
        const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
        const uploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET;

        if (!cloudName || !uploadPreset) {
          throw new Error('Image upload is not configured');
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', uploadPreset);

        const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        const data = await response.json();
        setLocalPreview(data.secure_url);
        onChange(data.secure_url);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed. Please try again.';
      setError(message);
      setLocalPreview(null);
      console.error('Image upload error:', err);
    } finally {
      setIsUploading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    setError(null);
    setLocalPreview(null);
    onChange('');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        {displayUrl ? (
          <div className="relative group">
            <div
              className={`rounded-lg overflow-hidden border-2 border-primary/20 bg-muted ${
                variant === 'card' ? 'w-40 h-56' : 'w-32 h-32'
              }`}
            >
              <img
                src={displayUrl}
                alt="Uploaded image"
                className={`w-full h-full ${variant === 'card' ? 'object-contain' : 'object-cover'}`}
              />
            </div>
            <button
              type="button"
              onClick={handleRemove}
              disabled={isUploading}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition shadow-sm disabled:opacity-50"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div
            className={`rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center bg-muted/30 ${
              variant === 'card' ? 'w-40 h-56' : 'w-32 h-32'
            }`}
          >
            {isUploading ? (
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            ) : (
              <Upload className="w-8 h-8 text-muted-foreground/50" />
            )}
          </div>
        )}

        <div className="flex-1">
          <label className="cursor-pointer">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              disabled={isUploading}
              className="hidden"
              data-testid={inputTestId}
            />
            <Button type="button" variant="secondary" size="sm" disabled={isUploading} asChild>
              <span>
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {displayUrl
                      ? buttonLabel
                        ? `Change ${buttonLabel}`
                        : 'Change Image'
                      : buttonLabel
                        ? `Upload ${buttonLabel}`
                        : 'Upload Image'}
                  </>
                )}
              </span>
            </Button>
          </label>
          <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 8MB</p>
          {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </div>
      </div>
    </div>
  );
}
